import PDFPage from './PDFPage'
import type { ReportData, BrandingConfig } from '@/types'
import { formatCurrency } from '@/utils/calculations'
import { ILC_WEALTH_UPLIFT, RESEARCH_SOURCES } from '@/data/valueOfAdvice'

interface ResearchEvidencePageProps {
  data: ReportData
  branding: BrandingConfig
  pageNumber: number
  totalPages: number
}

export default function ResearchEvidencePage(props: ResearchEvidencePageProps) {
  const { branding, pageNumber, totalPages } = props
  const isLight = branding.theme === 'light'
  const cardBg = isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)'
  const cardBorder = isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)'

  return (
    <PDFPage
      pageNumber={pageNumber}
      totalPages={totalPages}
      title="Research & Evidence"
      subtitle="Supporting Data"
      branding={branding}
    >
      {/* Introduction */}
      <div style={{ marginBottom: '24px' }}>
        <p style={{ fontSize: '13px', lineHeight: 1.6, opacity: 0.8 }}>
          The value of professional financial advice is supported by extensive research from leading
          financial institutions and academic studies. Here are the key findings that underpin our
          projections.
        </p>
      </div>

      {/* Research Sources Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        {RESEARCH_SOURCES.map((source, index) => (
          <div
            key={index}
            style={{
              background: cardBg,
              border: `1px solid ${cardBorder}`,
              borderRadius: '12px',
              padding: '16px',
            }}
          >
            <div style={{ fontSize: '10px', opacity: 0.6, marginBottom: '4px' }}>
              {source.year}
            </div>
            <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '8px' }}>
              {source.name}
            </div>
            <p style={{ fontSize: '11px', opacity: 0.7, margin: 0, lineHeight: 1.4 }}>
              {source.description}
            </p>
          </div>
        ))}
      </div>

      {/* ILC Study Deep Dive */}
      <div
        style={{
          background: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
        }}
      >
        <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>
          ILC/Royal London Study: Key Findings
        </h3>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '16px',
            marginBottom: '16px',
          }}
        >
          <div
            style={{
              background: isLight ? '#ffffff' : 'rgba(0,0,0,0.2)',
              borderRadius: '8px',
              padding: '16px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '11px', opacity: 0.6, marginBottom: '4px' }}>
              Pension Wealth Uplift
            </div>
            <div
              style={{ fontSize: '24px', fontWeight: 600, color: branding.primaryColor }}
            >
              {formatCurrency(ILC_WEALTH_UPLIFT.pensionWealth)}
            </div>
          </div>
          <div
            style={{
              background: isLight ? '#ffffff' : 'rgba(0,0,0,0.2)',
              borderRadius: '8px',
              padding: '16px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '11px', opacity: 0.6, marginBottom: '4px' }}>
              Non-Pension Wealth Uplift
            </div>
            <div
              style={{ fontSize: '24px', fontWeight: 600, color: branding.secondaryColor }}
            >
              {formatCurrency(ILC_WEALTH_UPLIFT.nonPensionWealth)}
            </div>
          </div>
        </div>

        {/* Insights */}
        <div style={{ borderTop: `1px solid ${cardBorder}`, paddingTop: '16px' }}>
          <h4 style={{ fontSize: '12px', fontWeight: 600, marginBottom: '12px' }}>
            Key Insights
          </h4>
          {ILC_WEALTH_UPLIFT.insights.map((insight, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                marginBottom: '8px',
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke={branding.primaryColor}
                strokeWidth="2"
                style={{ flexShrink: 0, marginTop: '2px' }}
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span style={{ fontSize: '12px', lineHeight: 1.4 }}>{insight}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Quote */}
      <div
        style={{
          background: `linear-gradient(135deg, ${branding.primaryColor}10 0%, ${branding.secondaryColor}10 100%)`,
          border: `1px solid ${branding.primaryColor}20`,
          borderRadius: '12px',
          padding: '20px',
          position: 'relative',
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill={branding.primaryColor}
          style={{ position: 'absolute', top: '12px', left: '16px', opacity: 0.2 }}
        >
          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
        </svg>
        <blockquote
          style={{
            fontSize: '14px',
            fontStyle: 'italic',
            lineHeight: 1.6,
            margin: '0 0 12px 0',
            paddingLeft: '32px',
          }}
        >
          &quot;A good financial adviser is like a good tennis coach. They won&apos;t make you play better
          shots, but they&apos;ll stop you playing bad ones.&quot;
        </blockquote>
        <div style={{ paddingLeft: '32px', fontSize: '12px', opacity: 0.7 }}>
          — Roger Federer analogy, commonly cited in financial advice research
        </div>
      </div>
    </PDFPage>
  )
}
